import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  getDocs,
  orderBy,
  limit,
  addDoc,
  serverTimestamp,
  Timestamp,
  DocumentData
} from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from './config'; 
import { writeBatch, increment, runTransaction } from 'firebase/firestore';
import { cache } from '../utils/cacheManager';

const LIBRARY_META_COLLECTION = 'libraryMeta';

// Invalidate dashboard-related localStorage caches and notify other tabs
export const invalidateDashboardCaches = (): void => {
  try {
    Object.keys(localStorage).forEach(key => {
      if (key.includes('dashboard_cache') || 
          key.includes('_members_cache') ||
          key.includes('_books_cache') ||
          key.includes('_categories_cache') ||
          key.includes('attendance_records_cache') ||
          key.includes('library_stats')) {
        localStorage.removeItem(key);
      }
    });

    // Broadcast to other tabs/windows so they can refresh
    try {
      window.dispatchEvent(new Event('dashboardCacheInvalidated'));
    } catch (e) {
      // ignore in non-browser contexts
    }
  } catch (err) {
    console.warn('Failed to invalidate dashboard caches', err);
  }
};

// Statistics document structure
export interface LibraryStats {
  totalMembers: number;
  activeMembers: number;
  maleMembers: number;
  femaleMembers: number;
  otherGenderMembers: number;
  totalBooks: number;
  availableBooks: number;
  totalAttendance: number;
  todayAttendance: number;
  totalBorrowings: number;
  lastUpdated: any;
  ageDistribution: {
    children: number;
    teenagers: number;
    youngAdults: number;
    senior: number;
  };
  categoryDistribution: Record<string, number>;
  monthlyAttendance: Record<string, number>;
}

// Initialize stats document if not exists
export const initializeLibraryStats = async (): Promise<{ success: boolean; error?: string }> => {
  try {
    const statsRef = doc(db, LIBRARY_META_COLLECTION, 'stats');
    const statsSnap = await getDoc(statsRef);
    
    if (!statsSnap.exists()) {
      await setDoc(statsRef, {
        totalMembers: 0,
        activeMembers: 0,
        maleMembers: 0,
        femaleMembers: 0,
        otherGenderMembers: 0,
        totalBooks: 0,
        availableBooks: 0,
        totalAttendance: 0,
        todayAttendance: 0,
        totalBorrowings: 0,
        ageDistribution: {
          children: 0,
          teenagers: 0,
          youngAdults: 0,
          senior: 0
        },
        categoryDistribution: {},
        monthlyAttendance: {},
        lastUpdated: serverTimestamp()
      });
    }
    
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

// Update statistics when member is created
export const updateStatsOnMemberCreate = async (memberData: LibraryMember): Promise<void> => {
  const statsRef = doc(db, LIBRARY_META_COLLECTION, 'stats');
  
  const updateData: any = {
    totalMembers: increment(1),
    lastUpdated: serverTimestamp()
  };
  
  // Update gender count
  if (memberData.gender === 'Male') {
    updateData.maleMembers = increment(1);
  } else if (memberData.gender === 'Female') {
    updateData.femaleMembers = increment(1);
  } else {
    updateData.otherGenderMembers = increment(1);
  }
  
  // Update status count
  if (memberData.status === 'Active') {
    updateData.activeMembers = increment(1);
  }
  
  // Update age distribution
  const age = calculateAgeFromDOB(memberData.dob);
  if (age <= 12) {
    updateData['ageDistribution.children'] = increment(1);
  } else if (age <= 17) {
    updateData['ageDistribution.teenagers'] = increment(1);
  } else if (age <= 24) {
    updateData['ageDistribution.youngAdults'] = increment(1);
  } else {
    updateData['ageDistribution.senior'] = increment(1);
  }
  
  await updateDoc(statsRef, updateData);
};

// Update statistics when member is deleted
export const updateStatsOnMemberDelete = async (memberData: LibraryMember): Promise<void> => {
  const statsRef = doc(db, LIBRARY_META_COLLECTION, 'stats');
  
  const updateData: any = {
    totalMembers: increment(-1),
    lastUpdated: serverTimestamp()
  };
  
  // Update gender count
  if (memberData.gender === 'Male') {
    updateData.maleMembers = increment(-1);
  } else if (memberData.gender === 'Female') {
    updateData.femaleMembers = increment(-1);
  } else {
    updateData.otherGenderMembers = increment(-1);
  }
  
  // Update status count
  if (memberData.status === 'Active') {
    updateData.activeMembers = increment(-1);
  }
  
  // Update age distribution
  const age = calculateAgeFromDOB(memberData.dob);
  if (age <= 12) {
    updateData['ageDistribution.children'] = increment(-1);
  } else if (age <= 17) {
    updateData['ageDistribution.teenagers'] = increment(-1);
  } else if (age <= 24) {
    updateData['ageDistribution.youngAdults'] = increment(-1);
  } else {
    updateData['ageDistribution.senior'] = increment(-1);
  }
  
  await updateDoc(statsRef, updateData);
};


// Update statistics when book is added
export const updateStatsOnBookCreate = async (bookData: LibraryBook): Promise<void> => {
  const statsRef = doc(db, LIBRARY_META_COLLECTION, 'stats');
  
  await updateDoc(statsRef, {
    totalBooks: increment(bookData.totalCopies || 1),
    availableBooks: increment(bookData.availableCopies || 1),
    lastUpdated: serverTimestamp()
  });
  
  // Update category distribution
  if (bookData.categoryId) {
    const categoryKey = `categoryDistribution.${bookData.categoryId}`;
    await updateDoc(statsRef, {
      [categoryKey]: increment(1)
    });
  }
};

// Update statistics when attendance is recorded
export const updateStatsOnAttendanceCreate = async (): Promise<void> => {
  const statsRef = doc(db, LIBRARY_META_COLLECTION, 'stats');
  const today = new Date().toISOString().split('T')[0];
  
  await updateDoc(statsRef, {
    totalAttendance: increment(1),
    todayAttendance: increment(1),
    [`monthlyAttendance.${today.substring(0, 7)}`]: increment(1),
    lastUpdated: serverTimestamp()
  });
};

// Calculate age from DOB
const calculateAgeFromDOB = (dob: string): number => {
  if (!dob) return 0;
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};




// Library Collections
export const LIBRARY_COLLECTIONS = {
  MEMBERS: 'libraryMembers',
  CATEGORIES: 'libraryCategories',
  BOOKS: 'libraryBooks',
  ATTENDANCE: 'libraryAttendance',
  BORROWINGS: 'libraryBorrowings',
  AUTHORS: 'libraryAuthors',
  PUBLISHERS: 'libraryPublishers'
};


// ------------------------------

// #region Member Management

export interface LibraryMember {
  id?: string;
  memId: string; // Changed from membId to memId (your actual field)
  fullName: string;
  dob: string; // Changed from dateOfBirth to dob
  gender: 'Male' | 'Female' | 'Other';
  registrationDate: string;
  contact: string; // Changed from phone to contact (your actual field)
  address: string;
  status: 'Active' | 'Inactive' | 'Suspended';
  originalPhotoPath?: string; // Your actual field
  photoUrl?: string; // Your actual field
  createdAt?: any;
  updatedAt?: any;
  
}

export const getLibraryMembers = async (filters?: {
  status?: string;
  gender?: string;
  search?: string;
  limitCount?: number;
}): Promise<{ success: boolean; data: LibraryMember[]; error?: string }> => {
  try {
    let constraints: any[] = [];
    
    // Order by registration date (descending) or by memId
    constraints.push(orderBy('registrationDate', 'desc'));
    constraints.push(limit(filters?.limitCount || 500));
    
    const q = query(collection(db, LIBRARY_COLLECTIONS.MEMBERS), ...constraints);
    const querySnapshot = await getDocs(q);
    
    const members: LibraryMember[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data() as DocumentData;
      
      // Normalize fields to match your exact database structure
      const normalized: LibraryMember = {
        id: doc.id,
        memId: data.memId || '', // Your exact field name
        fullName: data.fullName || '',
        dob: data.dob || '', // Your exact field name
        gender: data.gender || 'Other',
        registrationDate: data.registrationDate || '',
        contact: data.contact || '', // Your exact field name
        address: data.address || '',
        status: data.status || 'Inactive',
        originalPhotoPath: data.originalPhotoPath || null, // Your exact field
        photoUrl: data.photoUrl || null, // Your exact field
        createdAt: data.createdAt?.toDate?.() ?? data.createdAt ?? null,
        updatedAt: data.updatedAt?.toDate?.() ?? data.updatedAt ?? null,
      };

      members.push(normalized);
    });


    // Apply client-side filters
  // Apply client-side filters
    let filteredMembers = members;
    if (filters?.status && filters.status !== 'all') {
      const statusLower = String(filters.status).toLowerCase();
      filteredMembers = filteredMembers.filter(m => 
        m.status && String(m.status).toLowerCase() === statusLower
      );
    }
    if (filters?.gender && filters.gender !== 'all') {
      const genderLower = String(filters.gender).toLowerCase();
      filteredMembers = filteredMembers.filter(m => 
        m.gender && String(m.gender).toLowerCase() === genderLower
      );
    }


    // Apply search filter - FIXED: Check if contact exists before calling includes
     // Apply search filter - FIXED: Check if contact exists before calling includes
    if (filters?.search) {
      const searchLower = filters.search.toLowerCase();
      filteredMembers = filteredMembers.filter(member => {
        // Check each field safely
        const nameMatch = member.fullName?.toLowerCase().includes(searchLower) || false;
        const idMatch = member.memId?.toLowerCase().includes(searchLower) || false;
        const contactMatch = member.contact ? 
          (typeof member.contact === 'string' ? member.contact.includes(searchLower) : false) : 
          false;
        
        return nameMatch || idMatch || contactMatch;
      });
    }
    
    
    return { success: true, data: filteredMembers };
  } catch (error: any) {
    console.error('Error fetching library members:', error);
    return { success: false, error: error.message, data: [] };
  }
};

export const getLibraryMemberById = async (memberId: string): Promise<{ success: boolean; data?: LibraryMember; error?: string }> => {
  try {
    const cacheKey = `library_member.${memberId}`;

    // Use in-memory cache to avoid repeated reads within TTL
    const cached = await cache.get<LibraryMember | null>(cacheKey, async () => {
      // Firestore lookup (try by doc id first)
      const docRef = doc(db, LIBRARY_COLLECTIONS.MEMBERS, memberId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data() as DocumentData;
        return {
          id: docSnap.id,
          memId: data.memId || '',
          fullName: data.fullName || '',
          dob: data.dob || '',
          gender: data.gender || 'Other',
          registrationDate: data.registrationDate || '',
          contact: data.contact || '',
          address: data.address || '',
          status: data.status || 'Inactive',
          originalPhotoPath: data.originalPhotoPath || null,
          photoUrl: data.photoUrl || null,
          createdAt: data.createdAt?.toDate?.() ?? data.createdAt ?? null,
          updatedAt: data.updatedAt?.toDate?.() ?? data.updatedAt ?? null,
        } as LibraryMember;
      }

      // Fallback: query by memId field
      const q = query(collection(db, LIBRARY_COLLECTIONS.MEMBERS), where('memId', '==', memberId), limit(1));
      const qs = await getDocs(q);
      if (!qs.empty) {
        const d = qs.docs[0];
        const data = d.data() as DocumentData;
        return {
          id: d.id,
          memId: data.memId || '',
          fullName: data.fullName || '',
          dob: data.dob || '',
          gender: data.gender || 'Other',
          registrationDate: data.registrationDate || '',
          contact: data.contact || '',
          address: data.address || '',
          status: data.status || 'Inactive',
          originalPhotoPath: data.originalPhotoPath || null,
          photoUrl: data.photoUrl || null,
          createdAt: data.createdAt?.toDate?.() ?? data.createdAt ?? null,
          updatedAt: data.updatedAt?.toDate?.() ?? data.updatedAt ?? null,
        } as LibraryMember;
      }

      // Not found -> store null so subsequent calls within TTL avoid Firestore
      return null;
    }, 5 * 60 * 1000); // 5 minutes TTL

    if (cached) return { success: true, data: cached };
    return { success: false, error: 'Member not found' };
  } catch (error: any) {
    console.error('Error fetching library member:', error);
    return { success: false, error: error.message };
  }
};

export const getLibraryMemberByMemId = async (memId: string): Promise<{ success: boolean; data?: LibraryMember; error?: string }> => {
  try {
    // First try to read document by memId as doc id (supports members saved with memId as doc id)
    const docRef = doc(db, LIBRARY_COLLECTIONS.MEMBERS, memId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data() as DocumentData;
      const normalized: LibraryMember = {
        id: docSnap.id,
        memId: data.memId || data.mem_id || memId,
        fullName: data.fullName || '',
        dob: data.dob || '',
        gender: data.gender || 'Other',
        registrationDate: data.registrationDate || '',
        contact: data.contact || '',
        address: data.address || '',
        status: data.status || 'Inactive',
        originalPhotoPath: data.originalPhotoPath || null,
        photoUrl: data.photoUrl || null,
        createdAt: data.createdAt?.toDate?.() ?? data.createdAt ?? null,
        updatedAt: data.updatedAt?.toDate?.() ?? data.updatedAt ?? null,
      };

      return { success: true, data: normalized };
    }

    // Fallback: query by memId field
    const q = query(collection(db, LIBRARY_COLLECTIONS.MEMBERS), where('memId', '==', memId), limit(1));
    const qs = await getDocs(q);
    if (!qs.empty) {
      const d = qs.docs[0];
      const data = d.data() as DocumentData;
      const normalized: LibraryMember = {
        id: d.id,
        memId: data.memId || '',
        fullName: data.fullName || '',
        dob: data.dob || '',
        gender: data.gender || 'Other',
        registrationDate: data.registrationDate || '',
        contact: data.contact || '',
        address: data.address || '',
        status: data.status || 'Inactive',
        originalPhotoPath: data.originalPhotoPath || null,
        photoUrl: data.photoUrl || null,
        createdAt: data.createdAt?.toDate?.() ?? data.createdAt ?? null,
        updatedAt: data.updatedAt?.toDate?.() ?? data.updatedAt ?? null,
      };

      return { success: true, data: normalized };
    }

    return { success: false, error: 'Member not found' };
  } catch (error: any) {
    console.error('Error fetching library member by memId:', error);
    return { success: false, error: error.message };
  }
};

export const createLibraryMember = async (memberData: LibraryMember): Promise<{ success: boolean; id?: string; error?: string }> => {
  try {
    const batch = writeBatch(db);
    
    // Create member document
    let memberRef;
    if (memberData.memId) {
      memberRef = doc(db, LIBRARY_COLLECTIONS.MEMBERS, memberData.memId);
    } else {
      memberRef = doc(collection(db, LIBRARY_COLLECTIONS.MEMBERS));
    }
    
    // Prepare member payload (remove undefined fields)
    const payload: any = { ...memberData };
    Object.keys(payload).forEach(k => {
      if (payload[k] === undefined) delete payload[k];
    });

    batch.set(memberRef, {
      ...payload,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    // Commit member creation first. Stats update should not block member creation.
    await batch.commit();

    // Attempt to update statistics, but don't fail creation if stats update is denied
    try {
      await updateStatsOnMemberCreate(memberData);
    } catch (statsErr) {
      console.warn('Failed to update library stats after member create:', statsErr);
    }
    
    // Invalidate cache
    cache.invalidatePattern(/library_members.*/);
    cache.invalidatePattern(/library_stats.*/);
    cache.invalidatePattern(/dashboard.*/);

    // Also invalidate localStorage and notify listeners
    try { invalidateDashboardCaches(); } catch (e) { console.warn('invalidateDashboardCaches failed', e); }

    // Also invalidate any localStorage dashboard caches and notify other tabs
    try { invalidateDashboardCaches(); } catch (e) { console.warn('invalidateDashboardCaches failed', e); }
    
    return { success: true, id: memberRef.id };
  } catch (error: any) {
    console.error('Error creating library member:', error);
    return { success: false, error: error.message };
  }
};


export const updateLibraryMember = async (memberId: string, memberData: Partial<LibraryMember>): Promise<{ success: boolean; error?: string }> => {
  try {
    const docRef = doc(db, LIBRARY_COLLECTIONS.MEMBERS, memberId);
    await updateDoc(docRef, {
      ...memberData,
      updatedAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error: any) {
    console.error('Error updating library member:', error);
    return { success: false, error: error.message };
  }
};

export const deleteLibraryMember = async (memberId: string): Promise<{ success: boolean; error?: string }> => {
  try {
    // Get member data first to update statistics
    const memberDoc = await getDoc(doc(db, LIBRARY_COLLECTIONS.MEMBERS, memberId));
    if (memberDoc.exists()) {
      const memberData = memberDoc.data() as LibraryMember;
      // Try to update statistics, but don't fail deletion if stats update is unauthorized
      try {
        await updateStatsOnMemberDelete(memberData);
      } catch (statsError) {
        console.warn('updateStatsOnMemberDelete failed, continuing with deletion', statsError);
      }

      // Attempt to delete photo if exists but continue even if it fails
      if (memberData.originalPhotoPath) {
        try {
          await deleteMemberPhoto(memberData.originalPhotoPath);
        } catch (photoError) {
          console.warn('Failed to delete member photo, continuing with deletion', photoError);
        }
      }

      // Delete member document
      await deleteDoc(doc(db, LIBRARY_COLLECTIONS.MEMBERS, memberId));

      // Invalidate cache
      try {
        cache.invalidatePattern(/library_members.*/);
        cache.invalidatePattern(/library_stats.*/);
        cache.invalidatePattern(/dashboard.*/);
      } catch (cacheErr) {
        console.warn('Cache invalidation failed after delete', cacheErr);
      }

      return { success: true };
    }
    return { success: false, error: 'Member not found' };
  } catch (error: any) {
    console.error('Error deleting library member:', error);
    return { success: false, error: error.message };
  }
};
export const uploadMemberPhoto = async (
  file: File, 
  memberId: string, 
  existingPhotoPath?: string | null
): Promise<{ 
  success: boolean; 
  url?: string; 
  path?: string;
  error?: string 
}> => {
  try {
    const storage = getStorage();
    
    // If there's an existing photo, delete it first
    if (existingPhotoPath) {
      try {
        const oldRef = ref(storage, existingPhotoPath);
        await deleteObject(oldRef);
        console.log(`Deleted old photo at: ${existingPhotoPath}`);
      } catch (deleteError) {
        console.warn('Failed to delete old photo (may not exist):', deleteError);
        // Continue with upload even if delete fails
      }
    }
    
    // Generate consistent filename using memberId
    const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const fileName = `${memberId}_profile.${fileExtension}`;
    const storagePath = `member-photos/${fileName}`;
    const storageRef = ref(storage, storagePath);
    
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    
    return { success: true, url: downloadURL, path: storagePath };
  } catch (error: any) {
    console.error('Error uploading photo:', error);
    return { success: false, error: error.message };
  }
};

export const deleteMemberPhoto = async (photoPath: string): Promise<{ success: boolean; error?: string }> => {
  try {
    if (!photoPath) return { success: true };
    
    const storage = getStorage();
    const photoRef = ref(storage, photoPath);
    await deleteObject(photoRef);
    return { success: true };
  } catch (error: any) {
    console.warn('Error deleting photo:', error);
    return { success: false, error: error.message };
  }
};

// #endregion

// ------------------------------

// #region Category Management

export const deleteLibraryCategory = async (categoryId: string): Promise<{ success: boolean; error?: string }> => {
  try {
    await deleteDoc(doc(db, LIBRARY_COLLECTIONS.CATEGORIES, categoryId));
    return { success: true };
  } catch (error: any) {
    console.error('Error deleting library category:', error);
    return { success: false, error: error.message };
  }
};

export const updateLibraryCategory = async (categoryId: string, categoryData: Partial<LibraryCategory>): Promise<{ success: boolean; error?: string }> => {
  try {
    const docRef = doc(db, LIBRARY_COLLECTIONS.CATEGORIES, categoryId);
    await updateDoc(docRef, {
      ...categoryData,
      updatedAt: serverTimestamp(),
    });
    try { invalidateDashboardCaches(); } catch (e) { console.warn('invalidateDashboardCaches failed', e); }
    return { success: true };
  } catch (error: any) {
    console.error('Error updating library category:', error);
    return { success: false, error: error.message };
  }
};

export const getLibraryCategories = async (): Promise<{ success: boolean; data: LibraryCategory[]; error?: string }> => {
  try {
    const q = query(collection(db, LIBRARY_COLLECTIONS.CATEGORIES), orderBy('name', 'asc'));
    const querySnapshot = await getDocs(q);
    
    const categories: LibraryCategory[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      categories.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
      } as LibraryCategory);
    });
    
    return { success: true, data: categories };
  } catch (error: any) {
    console.error('Error fetching library categories:', error);
    return { success: false, error: error.message, data: [] };
  }
};

// #endregion

// ------------------------------

// #region Book Management

// Book Interface
export interface LibraryBook {
  availableCopies: number;
  id?: string;
  isbn: string;
  title: string;
  author: string;
  categoryId?: string; // For Firestore reference
  categoryName?: string; // For display
  imageUrl?: string;
  lastUpdated?: any;
  createdAt?: any;
  searchTerms?: string[];
  // Additional fields from your CSV
  imageFilename?: string; // Column 5 from CSV
  totalCopies?: number; // Column 4 from CSV (renamed to match your CSV)
  updatedAt: Date | any;
}

export const createLibraryBook = async (bookData: Partial<LibraryBook>): Promise<{ success: boolean; id?: string; error?: string }> => {
  try {
    const docRef = await addDoc(collection(db, LIBRARY_COLLECTIONS.BOOKS), {
      ...bookData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    // Invalidate caches after write
    try { invalidateDashboardCaches(); } catch (e) { console.warn('invalidateDashboardCaches failed', e); }
    return { success: true, id: docRef.id };
  } catch (error: any) {
    console.error('Error creating library book:', error);
    return { success: false, error: error.message };
  }
};

export const getLibraryBooks = async (limitCount: number = 500): Promise<{ success: boolean; data: LibraryBook[]; error?: string }> => {
  try {
    const q = query(collection(db, LIBRARY_COLLECTIONS.BOOKS), orderBy('createdAt', 'desc'), limit(limitCount));
    const querySnapshot = await getDocs(q);
    
    const books: LibraryBook[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      books.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
      } as LibraryBook);
    });
    
    return { success: true, data: books };
  } catch (error: any) {
    console.error('Error fetching library books:', error);
    return { success: false, error: error.message, data: [] };
  }
};

export const updateLibraryBook = async (bookId: string, bookData: Partial<LibraryBook>): Promise<{ success: boolean; error?: string }> => {
  try {
    const docRef = doc(db, LIBRARY_COLLECTIONS.BOOKS, bookId);
    await updateDoc(docRef, {
      ...bookData,
      updatedAt: serverTimestamp(),
    });
    try { invalidateDashboardCaches(); } catch (e) { console.warn('invalidateDashboardCaches failed', e); }
    return { success: true };
  } catch (error: any) {
    console.error('Error updating library book:', error);
    return { success: false, error: error.message };
  }
};

export const deleteLibraryBook = async (bookId: string): Promise<{ success: boolean; error?: string }> => {
  try {
    // Get book data first to check for image
    const bookDoc = await getDoc(doc(db, LIBRARY_COLLECTIONS.BOOKS, bookId));
    if (bookDoc.exists()) {
      const bookData = bookDoc.data() as LibraryBook;
      
      // Delete image if exists
      if (bookData.imageUrl) {
        await deleteBookCover(bookData.imageUrl);
      }
    }
    
    await deleteDoc(doc(db, LIBRARY_COLLECTIONS.BOOKS, bookId));
    try { invalidateDashboardCaches(); } catch (e) { console.warn('invalidateDashboardCaches failed', e); }
    return { success: true };
  } catch (error: any) {
    console.error('Error deleting library book:', error);
    return { success: false, error: error.message };
  }
};

export const uploadBookCover = async (
  file: File, 
  isbn: string, 
  existingImageUrl?: string
): Promise<{ success: boolean; url?: string; error?: string }> => {
  try {
    const storage = getStorage();
    
    // If there's an existing image, delete it first
    if (existingImageUrl) {
      try {
        // Extract path from URL or use known pattern
        const imagePath = existingImageUrl.split('/o/')[1]?.split('?')[0];
        if (imagePath) {
          const decodedPath = decodeURIComponent(imagePath);
          const oldRef = ref(storage, decodedPath);
          await deleteObject(oldRef);
        }
      } catch (error) {
        console.warn('Failed to delete old image:', error);
      }
    }
    
    // Generate filename
    const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const fileName = `${isbn.replace(/[^a-zA-Z0-9]/g, '_')}_cover.${fileExtension}`;
    const storagePath = `book-covers/${fileName}`;
    const storageRef = ref(storage, storagePath);
    
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    
    return { success: true, url: downloadURL };
  } catch (error: any) {
    console.error('Error uploading book cover:', error);
    return { success: false, error: error.message };
  }
};

export const deleteBookCover = async (imageUrl: string): Promise<{ success: boolean; error?: string }> => {
  try {
    if (!imageUrl) return { success: true };
    
    const storage = getStorage();
    const imagePath = imageUrl.split('/o/')[1]?.split('?')[0];
    if (imagePath) {
      const decodedPath = decodeURIComponent(imagePath);
      const photoRef = ref(storage, decodedPath);
      await deleteObject(photoRef);
    }
    return { success: true };
  } catch (error: any) {
    console.warn('Error deleting book cover:', error);
    return { success: false, error: error.message };
  }
};
// #endregion

// ------------------------------

// #region Attendance Management
export interface AttendanceRecord {
  id?: string;
  memberId: string;
  fullName: string;
  bookTitle: string;
  categoryId: string;
  categoryName?: string;
  timeRecorded: any; // Firestore Timestamp
  date: string; // YYYY-MM-DD format for easy querying
  monthYear: string; // YYYY-MM format
  createdAt?: any;
}

// Attendance Import Interface
export interface AttendanceImport {
  id?: string;
  monthYear: string;
  attendanceData: AttendanceRecord[];
  importDate: any;
  recordCount: number;
}

// Attendance Filter Interface
export interface AttendanceFilter {
  startDate?: Date;
  endDate?: Date;
  memberId?: string;
  categoryId?: string;
  bookTitle?: string;
}

// Attendance Management Functions
// Update the existing createAttendanceRecord function in libraryFirestoreService.ts

export const createAttendanceRecord = async (
  attendanceData: Omit<AttendanceRecord, 'id' | 'createdAt' | 'date' | 'monthYear'> & {
    categoryId: string;
  }
): Promise<{ success: boolean; id?: string; error?: string }> => {
  try {
    // Parse the timeRecorded string to Date
    const timeRecordedDate = attendanceData.timeRecorded instanceof Date 
      ? attendanceData.timeRecorded 
      : new Date(attendanceData.timeRecorded);
    
    // Extract date components for easy querying
    const dateStr = timeRecordedDate.toISOString().split('T')[0]; // YYYY-MM-DD
    const monthYear = timeRecordedDate.toISOString().substring(0, 7); // YYYY-MM
    
    // Get category name from categories collection if not provided
    let categoryName = attendanceData.categoryName;
    if (!categoryName && attendanceData.categoryId) {
      try {
        const catRef = doc(db, LIBRARY_COLLECTIONS.CATEGORIES, String(attendanceData.categoryId));
        const catSnap = await getDoc(catRef);
        if (catSnap.exists()) {
          const catData = catSnap.data() as any;
          categoryName = catData.name;
        } else {
          // fallback: try querying by numeric 'id' field if categories store an id property
          const categoryQuery = query(
            collection(db, LIBRARY_COLLECTIONS.CATEGORIES),
            where('id', '==', attendanceData.categoryId)
          );
          const categorySnapshot = await getDocs(categoryQuery);
          if (!categorySnapshot.empty) {
            const categoryData = categorySnapshot.docs[0].data();
            categoryName = categoryData.name;
          }
        }
      } catch (err) {
        console.warn('Could not resolve category name for id', attendanceData.categoryId, err);
      }
    }
    
    const docRef = await addDoc(collection(db, LIBRARY_COLLECTIONS.ATTENDANCE), {
      ...attendanceData,
      categoryName: categoryName || null,
      timeRecorded: Timestamp.fromDate(timeRecordedDate),
      date: dateStr,
      monthYear: monthYear,
      createdAt: serverTimestamp(),
    });
    // Invalidate dashboard caches (localStorage + broadcast)
    try { invalidateDashboardCaches(); } catch (e) { console.warn('invalidateDashboardCaches failed', e); }
    return { success: true, id: docRef.id };
  } catch (error: any) {
    console.error('Error creating attendance record:', error);
    return { success: false, error: error.message };
  }
};

// Update getAttendanceRecords function in libraryFirestoreService.ts
export const getAttendanceRecords = async (
  filters?: AttendanceFilter & { limitCount?: number }
): Promise<{ success: boolean; data: AttendanceRecord[]; error?: string }> => {
  try {
    let constraints: any[] = [orderBy('timeRecorded', 'desc')];
    
    // Use 'timeRecorded' instead of 'checkInTime'
    if (filters?.startDate && filters?.endDate) {
      constraints.push(where('timeRecorded', '>=', Timestamp.fromDate(filters.startDate)));
      constraints.push(where('timeRecorded', '<=', Timestamp.fromDate(filters.endDate)));
    }
    
    if (filters?.memberId) {
      constraints.push(where('memberId', '==', filters.memberId));
    }
    
    if (filters?.categoryId) {
      constraints.push(where('categoryId', '==', filters.categoryId));
    }
    
    if (filters?.bookTitle) {
      constraints.push(where('bookTitle', '==', filters.bookTitle));
    }

    constraints.push(limit(filters?.limitCount || 500));
    
    const q = query(collection(db, LIBRARY_COLLECTIONS.ATTENDANCE), ...constraints);
    const querySnapshot = await getDocs(q);
    
    const attendance: AttendanceRecord[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const normalizedMemberId =
        data.memberId ||
        data.memId ||
        data.member_id ||
        data.memberID ||
        data.MemberId ||
        '';
      const normalizedFullName =
        data.fullName ||
        data.full_name ||
        data.FullName ||
        '';
      attendance.push({
        id: doc.id,
        memberId: normalizedMemberId,
        fullName: normalizedFullName,
        bookTitle: data.bookTitle || '',
        categoryId: data.categoryId || '',
        categoryName: data.categoryName || '',
        timeRecorded: data.timeRecorded?.toDate() || new Date(),
        date: data.date || '',
        monthYear: data.monthYear || '',
        createdAt: data.createdAt?.toDate() || new Date(),
      } as AttendanceRecord);
    });
    
    return { success: true, data: attendance };
  } catch (error: any) {
    console.error('Error fetching attendance records:', error);
    return { success: false, error: error.message, data: [] };
  }
};

export const getAttendanceByDateRange = async (
  startDate: Date,
  endDate: Date
): Promise<{ success: boolean; data: AttendanceRecord[]; error?: string }> => {
  return getAttendanceRecords({ startDate, endDate });
};


export const getAttendanceByDate = async (
  date: Date
): Promise<{ success: boolean; data: AttendanceRecord[]; error?: string }> => {
  const startOfDay = new Date(date);
  startOfDay.setHours(0, 0, 0, 0);
  
  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59, 59, 999);
  
  return getAttendanceRecords({ startDate: startOfDay, endDate: endOfDay });
};

// Add this to your libraryFirestoreService.ts file in the #region Attendance Management section

export const updateAttendanceRecord = async (
  recordId: string,
  attendanceData: Partial<AttendanceRecord>
): Promise<{ success: boolean; error?: string }> => {
  try {
    const docRef = doc(db, LIBRARY_COLLECTIONS.ATTENDANCE, recordId);
    
    // Prepare update data
    const updateData: any = {
      ...attendanceData,
      updatedAt: serverTimestamp(),
    };
    
    // If timeRecorded is provided, update the date and monthYear fields
    if (attendanceData.timeRecorded) {
      const timeRecordedDate = attendanceData.timeRecorded instanceof Date 
        ? attendanceData.timeRecorded 
        : new Date(attendanceData.timeRecorded);
      
      updateData.timeRecorded = Timestamp.fromDate(timeRecordedDate);
      updateData.date = timeRecordedDate.toISOString().split('T')[0];
      updateData.monthYear = timeRecordedDate.toISOString().substring(0, 7);
    }
    
    // If categoryId is provided, also get categoryName
    if (attendanceData.categoryId !== undefined && attendanceData.categoryId !== null) {
      // Fetch category name if not provided
      if (!attendanceData.categoryName) {
        try {
          const catRef = doc(db, LIBRARY_COLLECTIONS.CATEGORIES, String(attendanceData.categoryId));
          const catSnap = await getDoc(catRef);
          if (catSnap.exists()) {
            const catData = catSnap.data() as any;
            updateData.categoryName = catData.name;
          } else {
            // fallback: try numeric 'id' field
            const categoryIdNum = Number(attendanceData.categoryId);
            if (!isNaN(categoryIdNum)) {
              const categoryQuery = query(
                collection(db, LIBRARY_COLLECTIONS.CATEGORIES),
                where('id', '==', categoryIdNum)
              );
              const categorySnapshot = await getDocs(categoryQuery);
              if (!categorySnapshot.empty) {
                const categoryData = categorySnapshot.docs[0].data();
                updateData.categoryName = categoryData.name;
              }
            }
          }
        } catch (err) {
          console.warn('Could not resolve category name for id', attendanceData.categoryId, err);
        }
      }
    }
    
    await updateDoc(docRef, updateData);
    try { invalidateDashboardCaches(); } catch (e) { console.warn('invalidateDashboardCaches failed', e); }
    return { success: true };
  } catch (error: any) {
    console.error('Error updating attendance record:', error);
    return { success: false, error: error.message };
  }
};

export const getAttendanceStats = async (
  startDate?: Date,
  endDate?: Date
): Promise<{ 
  success: boolean; 
  data: {
    totalRecords: number;
    uniqueMembers: number;
    booksRead: number;
    categories: Record<string, number>;
    dailyAverage: number;
  }; 
  error?: string 
}> => {
  try {
    let attendanceData: AttendanceRecord[] = [];
    
    if (startDate && endDate) {
      const result = await getAttendanceByDateRange(startDate, endDate);
      if (result.success) {
        attendanceData = result.data;
      }
    } else {
      const result = await getAttendanceRecords();
      if (result.success) {
        attendanceData = result.data;
      }
    }
    
    // Calculate statistics
    const uniqueMembers = new Set(attendanceData.map(record => record.memberId));
    const uniqueBooks = new Set(attendanceData.map(record => record.bookTitle));
    
    const categories: Record<string, number> = {};
    attendanceData.forEach(record => {
      const key = String(record.categoryId || '');
      categories[key] = (categories[key] || 0) + 1;
    });
    
    // Calculate daily average if we have date range
    let dailyAverage = 0;
    if (startDate && endDate) {
      const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
      dailyAverage = daysDiff > 0 ? attendanceData.length / daysDiff : 0;
    }
    
    return {
      success: true,
      data: {
        totalRecords: attendanceData.length,
        uniqueMembers: uniqueMembers.size,
        booksRead: uniqueBooks.size,
        categories,
        dailyAverage: Math.round(dailyAverage * 100) / 100,
      }
    };
  } catch (error: any) {
    console.error('Error fetching attendance stats:', error);
    return {
      success: false,
      error: error.message,
      data: {
        totalRecords: 0,
        uniqueMembers: 0,
        booksRead: 0,
        categories: {},
        dailyAverage: 0,
      }
    };
  }
};

export const importAttendanceFromCSV = async (
  csvData: string,
  monthYear: string
): Promise<{ 
  success: boolean; 
  importedCount: number; 
  error?: string;
  errors?: Array<{ record: any; error: string }>;
}> => {
  try {
    const errors: Array<{ record: any; error: string }> = [];
    let importedCount = 0;
    
    // Parse CSV - simple parsing for your structure
    const lines = csvData.split('\n');
    const headers = lines[0].split(',').map(h => h.trim());
    
    // Find attendance_data column (it contains JSON)
    const attendanceDataIndex = headers.findIndex(h => h === 'attendance_data');
    
    if (attendanceDataIndex === -1) {
      throw new Error('CSV format invalid: attendance_data column not found');
    }
    
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      const columns = line.split(',');
      const monthYearFromCSV = columns[0]?.replace(/"/g, '').trim();
      
      // Get the JSON string (might be in quotes)
      let jsonStr = columns.slice(attendanceDataIndex).join(',').trim();
      
      // Remove surrounding quotes if present
      if (jsonStr.startsWith('"') && jsonStr.endsWith('"')) {
        jsonStr = jsonStr.substring(1, jsonStr.length - 1);
      }
      
      try {
        // Parse the JSON array
        const attendanceArray = JSON.parse(jsonStr);
        
        // Import each attendance record
        for (const record of attendanceArray) {
          try {
            const timeRecordedDate = new Date(record.TimeRecorded);
            const dateStr = timeRecordedDate.toISOString().split('T')[0];
            
            await createAttendanceRecord({
              memberId: record.MemberId,
              fullName: record.FullName,
              bookTitle: record.BookTitle || '',
              categoryId: record.CategoryId,
              categoryName: record.CategoryName || null,
              timeRecorded: timeRecordedDate,
            });
            
            importedCount++;
          } catch (recordError: any) {
            errors.push({
              record,
              error: recordError.message || 'Unknown error'
            });
          }
        }
      } catch (parseError: any) {
        console.error(`Error parsing JSON on line ${i}:`, parseError);
        errors.push({
          record: { line: i, data: jsonStr.substring(0, 100) + '...' },
          error: parseError.message || 'JSON parse error'
        });
      }
    }
    
    // Create import log record
    await addDoc(collection(db, 'attendanceImports'), {
      monthYear,
      importDate: serverTimestamp(),
      recordCount: importedCount,
      errorCount: errors.length,
    });
    
    return { 
      success: errors.length === 0, 
      importedCount, 
      errors: errors.length > 0 ? errors : undefined,
      error: errors.length > 0 ? `Imported with ${errors.length} errors` : undefined
    };
  } catch (error: any) {
    console.error('Error importing attendance from CSV:', error);
    return { 
      success: false, 
      importedCount: 0, 
      error: error.message 
    };
  }
};

export const deleteAttendanceRecord = async (recordId: string): Promise<{ success: boolean; error?: string }> => {
  try {
    await deleteDoc(doc(db, LIBRARY_COLLECTIONS.ATTENDANCE, recordId));
    try { invalidateDashboardCaches(); } catch (e) { console.warn('invalidateDashboardCaches failed', e); }
    return { success: true };
  } catch (error: any) {
    console.error('Error deleting attendance record:', error);
    return { success: false, error: error.message };
  }
};
// #endregion






// Category Interface
export interface LibraryCategory {
  id?: string;
  name: string;
  description: string;
  color: string;
  icon?: string;
  bookCount: number;
  createdAt?: any;
  updatedAt?: any;
}






// deleteLibraryMember to also delete photo

// Book Operations

// Category Operations
export const createLibraryCategory = async (categoryData: LibraryCategory): Promise<{ success: boolean; id?: string; error?: string }> => {
  try {
    const docRef = await addDoc(collection(db, LIBRARY_COLLECTIONS.CATEGORIES), {
      ...categoryData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    try { invalidateDashboardCaches(); } catch (e) { console.warn('invalidateDashboardCaches failed', e); }
    return { success: true, id: docRef.id };
  } catch (error: any) {
    console.error('Error creating library category:', error);
    return { success: false, error: error.message };
  }
};





// Optimized getLibraryStatistics - reads from precomputed document
export const getLibraryStatistics = async (): Promise<{
  success: boolean;
  data: {
    totalMembers: number;
    activeMembers: number;
    totalBooks: number;
    availableBooks: number;
    todayAttendance: number;
    totalBorrowings: number;
  };
  error?: string;
}> => {
  try {
    const statsRef = doc(db, LIBRARY_META_COLLECTION, 'stats');
    const statsSnap = await getDoc(statsRef);
    
    if (statsSnap.exists()) {
      const data = statsSnap.data();
      return {
        success: true,
        data: {
          totalMembers: data.totalMembers || 0,
          activeMembers: data.activeMembers || 0,
          totalBooks: data.totalBooks || 0,
          availableBooks: data.availableBooks || 0,
          todayAttendance: data.todayAttendance || 0,
          totalBorrowings: data.totalBorrowings || 0,
        }
      };
    } else {
      // Initialize if doesn't exist
      await initializeLibraryStats();
      return {
        success: true,
        data: {
          totalMembers: 0,
          activeMembers: 0,
          totalBooks: 0,
          availableBooks: 0,
          todayAttendance: 0,
          totalBorrowings: 0
        }
      };
    }
  } catch (error: any) {
    console.error('Error fetching library statistics:', error);
    return {
      success: false,
      error: error.message,
      data: {
        totalMembers: 0,
        activeMembers: 0,
        totalBooks: 0,
        availableBooks: 0,
        todayAttendance: 0,
        totalBorrowings: 0
      }
    };
  }
};

// Note: getLibraryMemberByMemId is implemented above; no alias export needed.

